import React from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Gem, ArrowLeft } from "lucide-react";

const H2 = ({ children }) => (
  <h2 className="font-display text-2xl text-white mt-10 mb-3 tracking-tight">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="font-display text-lg text-white mt-6 mb-2">{children}</h3>
);
const P = ({ children }) => (
  <p className="text-zinc-300 leading-relaxed mb-4">{children}</p>
);
const UL = ({ children }) => (
  <ul className="text-zinc-300 leading-relaxed mb-4 pl-5 list-disc space-y-1.5">{children}</ul>
);

export default function Privacy() {
  return (
    <div className="min-h-screen" data-testid="privacy-page">
      <Navbar />
      <section className="pt-32 pb-24 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-gold mb-8" data-testid="privacy-back">
            <ArrowLeft className="w-4 h-4" /> Back home
          </Link>

          <div className="flex items-center gap-2.5 mb-4">
            <Gem className="w-6 h-6 text-gold" />
            <span className="label-eyebrow">Legal</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-white mb-3">
            Privacy Policy
          </h1>
          <p className="text-sm text-zinc-500 mb-10">
            Last updated: February 2026 · Contact: privacy@myleadsconvert.us
          </p>

          <P>
            My Leads Convert ("we", "us", "our") operates the platform at{" "}
            <span className="text-white">myleadsconvert.us</span>. The platform helps you define an Ideal
            Customer Profile (ICP), source real US nonprofit organizations from public IRS 990 filings via
            ProPublica, draft grounded outreach emails with AI assistance, and manage outreach campaigns.
            This policy explains how we collect, use, and protect personal information.
          </P>

          <div className="my-6 p-4 rounded-lg bg-emerald-500/[0.06] border border-emerald-500/25 text-sm text-emerald-100/90 leading-relaxed" data-testid="privacy-real-data-note">
            <div className="font-medium text-emerald-200 mb-1">Note on the real nonprofit data we surface</div>
            The organizations shown under "Mine real leads" are <span className="text-white">real</span>,{" "}
            <span className="text-white">US-registered</span> nonprofits pulled from ProPublica's public
            Nonprofit Explorer API. Their names, EINs, addresses, NTEE codes, and 990 filing amounts are
            public record because 501(c) organizations are required by federal law to file Form 990 with
            the IRS annually. This data is about <span className="text-white">legal entities</span> (organizations),
            not natural persons, and we do not fabricate individual contacts on top of it. Section 2A below
            spells out what this means for you and for the organizations we surface.
          </div>

          <H2>1. Information We Collect</H2>
          <H3>a. Information you provide</H3>
          <UL>
            <li>Account info: name, email, password (stored only as a one-way bcrypt hash), email-verification state.</li>
            <li>ICP inputs: industry, target role, company size, geography, keywords.</li>
            <li>Campaign content: subject lines and message bodies you author or save from batch drafts.</li>
            <li>Settings: your Demo-mode toggle and account preferences.</li>
            <li>Support communications you send us.</li>
          </UL>
          <H3>b. Information generated or sourced in your account</H3>
          <UL>
            <li>
              <span className="text-white">Real nonprofit records</span> ("Mine real leads"): sourced from
              ProPublica's public Nonprofit Explorer API. These are{" "}
              <span className="text-emerald-300">real, third-party organization records</span> — not
              content you generated and not your personal information. Each record contains only
              publicly-filed IRS Form 990 data: organization name, EIN, city/state, NTEE activity code,
              latest reported revenue, and 990 filing year, plus a link back to the ProPublica public
              record. We <span className="text-emerald-300">do not</span> fabricate contact names,
              emails, or personal information for these records. See Section 2A for the full sourcing
              breakdown and your responsibilities when acting on this data.
            </li>
            <li>
              <span className="text-white">AI-generated demo leads</span> (only when you enable Demo mode
              in Settings): plausible-sounding but{" "}
              <span className="text-amber-300">fabricated</span> lead records used for pitch decks, UI
              walkthroughs, and product testing. They are clearly badged "Demo" in the UI and never mix
              with real records.
            </li>
            <li>
              <span className="text-white">Uploaded contacts</span> ("Upload CSV / Excel"): contacts you
              import from a spreadsheet. Only the fields you map are sent to our server; the file itself
              is parsed in your browser. Uploaded leads carry a blue "Uploaded" badge. See Section 2B
              for the full storage, use, and compliance breakdown.
            </li>
            <li>
              <span className="text-white">Outreach drafts</span> ("Draft outreach email", "Batch draft"):
              subject line, body, and grounding disclaimer generated by Claude from the verified 990 data
              of the selected lead(s). Drafts are visible only to you and stored transiently unless you
              save them into a campaign.
            </li>
            <li>
              <span className="text-white">Activation timestamp</span>: the moment your first real
              ProPublica lead is added to your workspace is recorded once (<code className="text-zinc-400">first_mine_at</code>)
              and used only in aggregate, admin-only funnel analytics.
            </li>
          </UL>
          <H3>c. Information collected automatically</H3>
          <UL>
            <li>Server logs: IP address, browser type, pages visited, error traces.</li>
            <li>Authentication cookies: <code className="text-zinc-400">access_token</code> and <code className="text-zinc-400">refresh_token</code>, both HTTP-only, SameSite=Lax, and Secure on production HTTPS (controlled by the <code className="text-zinc-400">COOKIE_SECURE</code> environment flag).</li>
            <li>Local browser storage: two small flags (<code className="text-zinc-400">mlc-fresh-start-dismissed</code>, <code className="text-zinc-400">mlc-first-mine-celebrated</code>) used to remember that you've seen a one-time onboarding hint. No personal data, no tracking IDs.</li>
            <li>Basic device information.</li>
          </UL>
          <P>We do not run advertising trackers or behavioral-ad pixels.</P>

          <H2>2. How We Use Your Information</H2>
          <UL>
            <li>Provide, operate, and improve the platform.</li>
            <li>Authenticate you and secure your account.</li>
            <li>Query ProPublica's public API to source real nonprofit records that match your ICP.</li>
            <li>Generate grounded outreach drafts and (in Demo mode only) AI sample leads.</li>
            <li>Send transactional emails (email verification, password reset, campaign notifications) via Resend.</li>
            <li>Compute aggregate, admin-only activation funnel analytics (total users vs. activated users, median time-to-first-real-lead). No individual behavior is exposed to other users.</li>
            <li>Respond to support requests.</li>
            <li>Detect fraud and abuse; comply with legal obligations.</li>
          </UL>
          <P>
            <span className="text-white">We do not sell your personal information.</span> We do not use your
            ICP, saved leads, or campaign content to enrich any shared database available to other customers.
          </P>

          <H2>2A. Real Nonprofit Data — Sourcing, Rights &amp; Responsibilities</H2>
          <P>
            Because the leads under "Mine real leads" are real US-registered nonprofits (not fabricated
            samples), this section clarifies what that means for both you and the organizations we
            surface. This section is specific to ProPublica-sourced records; it does not apply to opt-in
            Demo-mode AI leads or to contacts you upload yourself (see Section 2B below).
          </P>

          <H3>a. Source, scope, and attribution</H3>
          <UL>
            <li>
              <span className="text-white">Source</span>: ProPublica's Nonprofit Explorer API, a public
              dataset built from filings that 501(c) organizations submit to the US Internal Revenue
              Service on Form 990. See <a href="https://projects.propublica.org/nonprofits/" target="_blank" rel="noreferrer" className="text-emerald-300 hover:underline">projects.propublica.org/nonprofits</a>.
            </li>
            <li>
              <span className="text-white">What we surface</span>: organization name, EIN, city/state,
              NTEE activity code, latest reported revenue amount, 990 filing year, and a link back to the
              organization's ProPublica public record. Every real lead in your workspace carries a green
              "Real" badge and links to its ProPublica page so you can verify the underlying filing.
            </li>
            <li>
              <span className="text-white">What we do NOT surface</span>: individual staff names,
              personal email addresses, direct phone numbers, LinkedIn URLs, or any other information
              about specific people at these organizations. Contact fields are left blank on real leads
              (the UI shows "Contact TBD") — you are responsible for researching the right person to
              reach on the organization's own website before sending any outreach.
            </li>
            <li>
              <span className="text-white">Real data is not "your" data</span>: real nonprofit records
              are third-party public records about legal entities. They are not personal information
              belonging to you as a user of the platform. You do not acquire ownership of the underlying
              public data by mining it into your workspace; you gain a copy that lives in your workspace
              until you delete it.
            </li>
          </UL>

          <H3>b. Legal basis &amp; nature of the data</H3>          <UL>
            <li>
              Form 990 filings are public records mandated by the US Internal Revenue Code (26 U.S.C.
              § 6104) and made available by the IRS. ProPublica republishes them under its Nonprofit
              Explorer service.
            </li>
            <li>
              Nonprofit organizations are <span className="text-white">legal entities</span>. In most
              jurisdictions (including under the CCPA/CPRA and GDPR), information about legal entities
              is not "personal information" or "personal data". However, some fields (like an executive
              director's name embedded in a public 990) may relate to identifiable individuals — where
              that occurs, those individuals have chosen to be listed on a public tax filing.
            </li>
            <li>
              We do not enrich, augment, cross-reference, or otherwise expand ProPublica records against
              other databases. What ProPublica exposes publicly is what you see.
            </li>
          </UL>

          <H3>c. ProPublica's terms and API etiquette</H3>
          <UL>
            <li>
              Your use of real nonprofit records is subject to ProPublica's{" "}
              <a href="https://www.propublica.org/legal/" target="_blank" rel="noreferrer" className="text-emerald-300 hover:underline">Terms of Use</a>{" "}
              and Nonprofit Explorer terms in addition to this policy. We do not warrant the completeness,
              timeliness, or accuracy of ProPublica's data — filings can be years out of date and
              typographical errors in 990 submissions are common.
            </li>
            <li>
              We send ProPublica only the minimum needed to run your search: a keyword and (optionally)
              a US state filter. We identify ourselves with a polite User-Agent header. Per-user rate
              limiting on our side protects ProPublica from unintended abuse.
            </li>
          </UL>

          <H3>d. Your responsibilities when contacting organizations we surface</H3>
          <P>
            When you send outreach to a real nonprofit sourced through the platform, you — not My Leads
            Convert — are the sender. You are responsible for:
          </P>
          <UL>
            <li>
              Complying with all applicable outreach laws in your jurisdiction and the recipient's:{" "}
              <span className="text-white">CAN-SPAM Act</span> (US commercial email), CASL (Canada),
              GDPR/PECR (EU/UK), and any state or sector-specific rules.
            </li>
            <li>
              Honoring unsubscribe requests, "do not contact" preferences, and the organization's
              published contact policies (many nonprofits publish specific channels for solicitations,
              partnerships, or press).
            </li>
            <li>
              Verifying that a listed 990 revenue or address is still current before using it in outreach
              copy. Filings can lag reality by 12–24 months.
            </li>
            <li>
              Reading the AI-drafted email before you send it. Every outreach draft ships with a
              "what was verified vs. not" disclaimer for exactly this reason.
            </li>
            <li>
              Not misrepresenting your affiliation with, or endorsement by, any organization we surface.
            </li>
          </UL>

          <H3>e. If an organization asks to be removed</H3>
          <P>
            Because the underlying data is public IRS record, we cannot remove an organization from the
            IRS 990 filings or from ProPublica's public index. What we can do is: (i) block that
            organization from appearing in any user's future "Mine real leads" results, and (ii) delete
            existing copies of that record from active user workspaces to the extent feasible. If you
            represent a nonprofit that would like to be suppressed from the platform, email us at{" "}
            <span className="text-white">privacy@myleadsconvert.us</span> with your organization name
            and EIN, and we will process the request within 30 days.
          </P>

          <H2>2B. Uploaded Contacts — Your Spreadsheet, Your Responsibility</H2>
          <P>
            When you upload a spreadsheet of contacts, we store those rows in your workspace as leads
            (tagged with a blue "Uploaded" badge). Uploaded contacts often include real personal email
            addresses — treat them accordingly.
          </P>
          <UL>
            <li>
              <span className="text-white">Storage</span>: only mapped fields for the rows you commit
              are sent to our server. The file itself is parsed in your browser and is not stored on
              our servers.
            </li>
            <li>
              <span className="text-white">Use</span>: we do not use uploaded contacts for any purpose
              other than serving them back to you inside your workspace. We do not enrich them, do not
              build shared databases from them, and do not share them with other customers.
            </li>
            <li>
              <span className="text-white">Compliance is on you</span>: You are the sender of any
              outreach. You must have permission or a legitimate basis to contact the individuals in
              your uploaded list under CAN-SPAM (US), CASL (Canada), GDPR/PECR (EU/UK), and any other
              rules that apply. The upload screen shows a reminder at commit time.
            </li>
            <li>
              <span className="text-white">Delete controls</span>: you can undo an entire upload within
              24 hours of committing it (one-click "Undo import"). After that, use the drawer's Delete,
              bulk-delete from selection, or the Clean up menu (which recognises an "Uploaded" scope in
              the same way as "Real" or "Demo").
            </li>
            <li>
              <span className="text-white">Requests from data subjects</span>: if a person contacts us
              asking us to erase their data, we will locate and delete all copies of that email address
              across user workspaces (within the timeframes required by law) and notify affected users.
            </li>
          </UL>

          <H2>3. AI and Machine Learning</H2>
          <P>
            When you use AI features, we send tightly-scoped inputs to Anthropic (Claude Sonnet 4.5, via the
            Emergent Universal Key). Specifically:
          </P>
          <UL>
            <li>
              <span className="text-white">For outreach drafting</span> ("Draft outreach email" and
              "Batch draft"): the target organization's public 990 facts (name, city/state, EIN, NTEE
              code, latest revenue year), plus your own name, industry, keywords, and role focus from
              your ICP. We do <span className="text-emerald-300">not</span> send recipient names, private
              email addresses, or inbox contents. The system prompt explicitly forbids Claude from
              inventing recipient names or programs.
            </li>
            <li>
              <span className="text-white">For Demo-mode AI leads</span> (opt-in): your ICP fields only.
            </li>
          </UL>
          <P>
            Our AI provider does not train foundation models on your inputs when used through the Emergent
            Universal Key.
          </P>

          <H2>4. Cookies &amp; Local Storage</H2>
          <P>
            We use only strictly-necessary authentication cookies (see Section 1c). Deleting them will log
            you out. We also use two small local-storage flags to remember that you've dismissed a one-time
            onboarding hint or already seen your first-mine celebration; clearing your browser storage will
            reset them. If we ever add optional analytics cookies, we will present a consent banner first.
          </P>

          <H2>5. Third-party Data Sources &amp; Sub-processors</H2>
          <H3>a. Data sources (read-only)</H3>
          <UL>
            <li>
              <span className="text-white">ProPublica Nonprofit Explorer</span> — public IRS 990 filing
              data. When you click "Mine real leads", we send your ICP keyword and optional US state to
              ProPublica's public search API and receive matching organization records. We do not send
              your personal information; ProPublica will however see our server's IP address as a normal
              part of any HTTPS request. Your use of this data is also subject to ProPublica's Terms of
              Use. See Section 2A for the full breakdown of what is and isn't sourced, and your
              responsibilities when acting on real nonprofit records.
            </li>
          </UL>
          <H3>b. Service sub-processors</H3>
          <P>We share information only with these service providers, strictly as needed to operate the platform:</P>
          <UL>
            <li><span className="text-white">MongoDB Atlas</span> — primary database.</li>
            <li><span className="text-white">Emergent</span> — application hosting and LLM key routing.</li>
            <li><span className="text-white">Anthropic</span> — AI drafting and (in Demo mode) sample lead generation.</li>
            <li><span className="text-white">Resend</span> — transactional email delivery.</li>
            <li><span className="text-white">Cloudflare / DNS providers</span> — routing and DDoS protection.</li>
          </UL>

          <H2>6. Data Retention &amp; Your Controls</H2>
          <UL>
            <li>Account data: retained while your account is active.</li>
            <li>Real, AI-demo, and seed leads: retained until you delete them. You can delete a single lead from the drawer, bulk-delete selected leads, or use the "Clean up" menu to wipe demo, archived, seed, or all leads at once.</li>
            <li>Outreach drafts: only persisted if you explicitly save them into a campaign; otherwise they exist only for the lifetime of the response.</li>
            <li>Password-reset and email-verification tokens: automatically purged (1 hour and 24 hours respectively).</li>
            <li>Server logs: typically 30 days.</li>
          </UL>

          <H2>7. Security</H2>
          <UL>
            <li>Passwords hashed with bcrypt.</li>
            <li>TLS/HTTPS for data in transit; encryption at rest via infrastructure providers.</li>
            <li>HTTP-only, Secure (in production), SameSite=Lax session cookies.</li>
            <li>Rate limiting and brute-force lockout on authentication endpoints.</li>
            <li>Per-user rate limiting on the lead-mining endpoints to prevent abuse of ProPublica and our AI budget.</li>
            <li>Single-use, SHA-256-hashed tokens for password reset and email verification.</li>
            <li>Role-based access controls: only <span className="text-white">platform operators</span> (the confirmed admin accounts on the account) can view the aggregate activation funnel and manage user-level cleanup. As of publication, the current platform operators are the accounts controlled by the site owner and one collaborating consultant.</li>
            <li>Admin account credentials are set by the account owner — the platform does not auto-reset admin passwords.</li>
          </UL>
          <P>No system is fully secure. Report suspected issues to privacy@myleadsconvert.us.</P>

          <H2>8. Your Rights</H2>
          <P>Depending on your jurisdiction, you may have the right to access, correct, delete, restrict, port, or object to processing of your personal data, and to withdraw consent. Many of these can be exercised in-app: you can update your name/email in Settings, self-delete every lead in your workspace via the "Clean up" menu, and delete your account by contacting us. For anything else, email <span className="text-white">privacy@myleadsconvert.us</span>. We respond within timeframes required by law (typically 30 days).</P>

          <H2>9. Children</H2>
          <P>The platform is intended for professionals over the age of 18 and is not directed to children. We do not knowingly collect personal information from anyone under 16.</P>

          <H2>10. International Transfers</H2>
          <P>The platform is hosted in the United States. If you access it from elsewhere, your data may be transferred to and processed in the US and other jurisdictions where our providers operate. Where required, we rely on Standard Contractual Clauses and the EU-U.S. Data Privacy Framework.</P>

          <H2>11. California &amp; EU Residents</H2>
          <P>California residents have rights under the CCPA/CPRA including to know, delete, correct, and opt-out of sale/share of personal information (we do not sell or share). EU/UK/Swiss residents have rights under GDPR/UK-GDPR and may lodge complaints with a local data-protection authority.</P>

          <H2>12. No Payments</H2>
          <P>My Leads Convert is currently a free MVP. We do not process payments, store payment card numbers, or run any pricing tier. If we add paid plans in the future, we will update this policy and disclose any payment processor before charging.</P>

          <H2>13. Changes to This Policy</H2>
          <P>We may update this policy from time to time. Material changes will be posted here with an updated "Last updated" date. Continued use of the platform after a change constitutes acceptance.</P>

          <H2>14. Contact</H2>
          <P>Questions or requests: <span className="text-white">privacy@myleadsconvert.us</span></P>

          <div className="mt-16 p-4 rounded-lg bg-white/[0.02] border border-white/5 text-xs text-zinc-500 leading-relaxed">
            This policy is provided as a good-faith description of our current data practices and is not
            legal advice. Please consult a privacy attorney qualified in your jurisdiction before treating
            it as a binding legal document.
          </div>
        </div>
      </section>
    </div>
  );
}
